/**
 * Same-origin opaque-session fetch helpers shared by GRUV web frontends.
 *
 * Gateway keeps Keycloak tokens server-side; browsers only send the session cookie.
 * On 401 this module tries POST /api/auth/refresh (single-flight, with a short
 * retry on transport/5xx blips), retries the original request once after a
 * successful refresh, then notifies a registered handler at most once so apps
 * can clear client state and redirect to login without retry storms.
 */

/** Callback invoked once when the opaque session is confirmed dead. */
export type SessionExpiredHandler = () => void;

/** Decides whether a remaining 401 should be treated as session expiry. */
export type IsSessionUnauthorized = (response: Response, url: string) => boolean | Promise<boolean>;

/** Options for {@link createSessionFetch}. */
export interface CreateSessionFetchOptions {
  /** Underlying fetch implementation (defaults to `globalThis.fetch`). */
  fetch?: typeof fetch;
  /**
   * When a 401 remains after refresh failure or a failed retry, decide whether it
   * means session death. Defaults to true for non-auth-bootstrap paths.
   */
  isSessionUnauthorized?: IsSessionUnauthorized;
}

/** How many times to attempt `/api/auth/refresh` when failures look transient. */
const REFRESH_ATTEMPTS = 2;

/** Delay between transient refresh retries (infra blip absorption). */
const REFRESH_RETRY_DELAY_MS = 1_000;

type RefreshAttemptResult = "ok" | "auth_dead" | "transient";

let sessionExpiredHandler: SessionExpiredHandler | null = null;
let sessionExpiredNotified = false;
let refreshInFlight: Promise<boolean> | null = null;

/** Registers the app callback invoked once when the opaque session is dead. */
export function setSessionExpiredHandler(handler: SessionExpiredHandler | null): void {
  sessionExpiredHandler = handler;
  sessionExpiredNotified = false;
}

/** Clears the one-shot guard so a later expiry can notify again (tests / re-login). */
export function resetSessionExpiredNotification(): void {
  sessionExpiredNotified = false;
}

function notifySessionExpired(): void {
  if (sessionExpiredNotified) {
    return;
  }
  sessionExpiredNotified = true;
  try {
    sessionExpiredHandler?.();
  } catch {
    // Session-expiry redirects must never break the original fetch caller.
  }
}

function resolveRequestUrl(input: RequestInfo | URL): string {
  if (typeof input === "string") {
    return input;
  }
  if (input instanceof URL) {
    return input.toString();
  }
  return input.url;
}

/**
 * Returns true for gateway auth bootstrap routes that must not trigger
 * session-expired redirects (login, refresh, session probe, logout, authorize).
 */
export function isAuthBootstrapPath(url: string): boolean {
  try {
    const path = url.startsWith("http") ? new URL(url).pathname : (url.split("?")[0] ?? url);
    return path === "/api/auth/refresh"
      || path === "/api/auth/session"
      || path === "/api/auth/login"
      || path === "/api/auth/logout"
      || path === "/api/auth/authorize"
      || path.startsWith("/api/auth/");
  } catch {
    return false;
  }
}

function isAuthDeathStatus(status: number): boolean {
  return status === 401 || status === 403;
}

function isTransientRefreshStatus(status: number): boolean {
  return status >= 500 || status === 408 || status === 429;
}

async function wait(ms: number): Promise<void> {
  await new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });
}

async function attemptRefreshOnce(fetchImpl: typeof fetch): Promise<RefreshAttemptResult> {
  try {
    const response = await fetchImpl("/api/auth/refresh", {
      method: "POST",
      credentials: "include",
    });
    if (response.ok) {
      return "ok";
    }
    if (isAuthDeathStatus(response.status)) {
      return "auth_dead";
    }
    if (isTransientRefreshStatus(response.status)) {
      return "transient";
    }
    // Unexpected 4xx from refresh is not recoverable by waiting.
    return "auth_dead";
  } catch {
    return "transient";
  }
}

/**
 * Attempts opaque-session refresh with a single-flight lock.
 * Retries only on transport/5xx-style failures; 401/403 fail immediately.
 */
async function refreshOpaqueSession(fetchImpl: typeof fetch): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      for (let attempt = 1; attempt <= REFRESH_ATTEMPTS; attempt += 1) {
        const result = await attemptRefreshOnce(fetchImpl);
        if (result === "ok") {
          return true;
        }
        if (result === "auth_dead") {
          return false;
        }
        if (attempt < REFRESH_ATTEMPTS) {
          await wait(REFRESH_RETRY_DELAY_MS);
        }
      }
      return false;
    })().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/**
 * Creates a fetch wrapper that includes credentials, retries once after
 * `POST /api/auth/refresh` on 401, and notifies the registered session-expired
 * handler at most once when the session cannot be recovered.
 */
export function createSessionFetch(options: CreateSessionFetchOptions = {}): typeof fetch {
  const resolveFetch = (): typeof fetch => {
    if (options.fetch) {
      return options.fetch;
    }
    return globalThis.fetch.bind(globalThis);
  };
  const isSessionUnauthorized = options.isSessionUnauthorized;

  const sessionFetch = async (
    input: RequestInfo | URL,
    init?: RequestInit,
    retried = false,
  ): Promise<Response> => {
    const fetchImpl = resolveFetch();
    const url = resolveRequestUrl(input);
    const response = await fetchImpl(input, {
      ...init,
      credentials: "include",
    });

    if (response.status !== 401 || isAuthBootstrapPath(url)) {
      return response;
    }

    // Allow callers to treat some 401s as domain failures (e.g. passport validation).
    if (isSessionUnauthorized) {
      const treatAsExpiry = await isSessionUnauthorized(response, url);
      if (!treatAsExpiry) {
        return response;
      }
    }

    if (!retried) {
      const refreshed = await refreshOpaqueSession(resolveFetch());
      if (refreshed) {
        return sessionFetch(input, init, true);
      }
    }

    notifySessionExpired();
    return response;
  };

  return ((input: RequestInfo | URL, init?: RequestInit) =>
    sessionFetch(input, init, false)) as typeof fetch;
}
